// Shared helpers for searching customers via the `/users/customer/list` API.
//
// These functions implement the same matching/sorting heuristics used in
// `CustomerSelector` so that any other dropdown driven by the customer-list
// endpoint (e.g. `MembershipClientDropdown`) behaves identically.

export const normalizePhoneNumber = (phone) => {
	if (!phone) return "";
	return String(phone).replace(/\D/g, "");
};

export const isPhoneNumber = (str) => {
	if (!str || !String(str).trim()) return false;
	const digitsOnly = String(str).replace(/[\s\-\+\(\)]/g, "");
	return /^\d{7,}$/.test(digitsOnly);
};

/**
 * Returns true when `customer` matches the free-text `searchText`.
 * Matches name, lastname, full name, contact number (raw + normalized digits)
 * and membership number — so membership dropdowns can search by cashless id.
 */
export const customerMatchesSearch = (customer, searchText) => {
	if (!searchText || !String(searchText).trim()) return true;
	if (!customer) return false;

	const query = String(searchText).trim().toLowerCase();

	const name = (customer.name || "").toLowerCase().trim();
	const lastname = (customer.lastname || "").toLowerCase().trim();
	const fullName = `${name} ${lastname}`.trim();
	const contactNumber = (customer.contact_number || "").toLowerCase().trim();
	const membershipNumber = normalizeMembershipNumberForApi(customer.membership_number).toLowerCase();

	if (!name && !lastname && !contactNumber && !membershipNumber) {
		return false;
	}

	if (
		(name && name.includes(query)) ||
		(lastname && lastname.includes(query)) ||
		(fullName && fullName.includes(query))
	) {
		return true;
	}

	// Normalize the query too so searches like "#242829" match stored "242829"
	const membershipQuery = normalizeMembershipNumberForApi(query).toLowerCase();
	if (membershipNumber && membershipQuery && membershipNumber.includes(membershipQuery)) {
		return true;
	}

	const normalizedQuery = normalizePhoneNumber(query);
	const normalizedContactNumber = normalizePhoneNumber(contactNumber);
	if (
		normalizedQuery &&
		normalizedContactNumber &&
		normalizedContactNumber.includes(normalizedQuery)
	) {
		return true;
	}
	if (contactNumber && contactNumber.includes(query)) {
		return true;
	}

	return false;
};

/** Sort customers by how strongly they match the search text. */
export const sortCustomersByRelevance = (customers, searchText) => {
	if (!searchText || !String(searchText).trim()) return customers;

	const query = String(searchText).trim().toLowerCase();
	const normalizedQuery = normalizePhoneNumber(query);
	const membershipQuery = normalizeMembershipNumberForApi(query).toLowerCase();

	return [...customers].sort((a, b) => {
		const aName = (a.name || "").toLowerCase();
		const aLastname = (a.lastname || "").toLowerCase();
		const aFullName = `${aName} ${aLastname}`.trim();
		const aContactNumber = (a.contact_number || "").toLowerCase();
		const aNormalizedContact = normalizePhoneNumber(aContactNumber);

		const bName = (b.name || "").toLowerCase();
		const bLastname = (b.lastname || "").toLowerCase();
		const bFullName = `${bName} ${bLastname}`.trim();
		const bContactNumber = (b.contact_number || "").toLowerCase();
		const bNormalizedContact = normalizePhoneNumber(bContactNumber);

		const aNameIndex = aName.includes(query) ? aName.indexOf(query) : Number.POSITIVE_INFINITY;
		const bNameIndex = bName.includes(query) ? bName.indexOf(query) : Number.POSITIVE_INFINITY;
		if (aNameIndex !== bNameIndex) return aNameIndex - bNameIndex;

		const aLastnameIndex = aLastname.includes(query)
			? aLastname.indexOf(query)
			: Number.POSITIVE_INFINITY;
		const bLastnameIndex = bLastname.includes(query)
			? bLastname.indexOf(query)
			: Number.POSITIVE_INFINITY;
		if (aLastnameIndex !== bLastnameIndex) return aLastnameIndex - bLastnameIndex;

		const aFullNameIndex = aFullName.includes(query)
			? aFullName.indexOf(query)
			: Number.POSITIVE_INFINITY;
		const bFullNameIndex = bFullName.includes(query)
			? bFullName.indexOf(query)
			: Number.POSITIVE_INFINITY;
		if (aFullNameIndex !== bFullNameIndex) return aFullNameIndex - bFullNameIndex;

		const aContactMatch =
			normalizedQuery &&
			aNormalizedContact &&
			(aNormalizedContact.includes(normalizedQuery) || aContactNumber.includes(query));
		const bContactMatch =
			normalizedQuery &&
			bNormalizedContact &&
			(bNormalizedContact.includes(normalizedQuery) || bContactNumber.includes(query));
		if (aContactMatch && !bContactMatch) return -1;
		if (!aContactMatch && bContactMatch) return 1;

		const aMembershipNumber = normalizeMembershipNumberForApi(a.membership_number).toLowerCase();
		const bMembershipNumber = normalizeMembershipNumberForApi(b.membership_number).toLowerCase();
		const aMembershipMatch =
			membershipQuery && aMembershipNumber && aMembershipNumber.includes(membershipQuery);
		const bMembershipMatch =
			membershipQuery && bMembershipNumber && bMembershipNumber.includes(membershipQuery);
		if (aMembershipMatch && !bMembershipMatch) return -1;
		if (!aMembershipMatch && bMembershipMatch) return 1;

		return 0;
	});
};

/** Stable cache-key fragment for `extraParams` passed to customer-list requests. */
export const serializeCustomerListExtraParams = (params) => {
	if (!params || typeof params !== "object") return "";
	try {
		return JSON.stringify(params, Object.keys(params).sort());
	} catch {
		return "";
	}
};

/** Extra fields for membership member search (e.g. ConfirmationPage others-membership flow). */
export const buildMembershipCustomerListExtraParams = (userId) => ({
	membership_status: "active",
	...(userId != null && userId !== "" ? { user: userId } : {}),
});

/** Build the request payload used by `/users/customer/list`. */
export const buildCustomerListRequest = (searchText = "", cursor = null, extraParams = {}) => {
	const searchForThisCall = String(searchText || "").trim();
	let apiSearchText = searchForThisCall;

	const normalizedDigits = normalizePhoneNumber(searchForThisCall);
	if (normalizedDigits && normalizedDigits.length >= 4) {
		const isLikelyPhoneNumber =
			isPhoneNumber(searchForThisCall) ||
			(normalizedDigits.length >= 4 && /^[\d\s\-\+\(\)]+$/.test(searchForThisCall));
		if (isLikelyPhoneNumber) {
			apiSearchText = normalizedDigits;
		}
	}

	const item = {
		pagination: 1,
		status: 1,
		search_text: apiSearchText,
		...extraParams,
	};
	if (!apiSearchText) {
		item.cursor_paginate = 1;
	}
	if (cursor) {
		item.cursor = cursor;
	}
	return { item, searchForThisCall };
};

/**
 * Map a customer record (from `/users/customer/list`) to the shape used by
 * membership/credit consumers like `ConfirmationPage` and `MembershipClientDropdown`.
 *
 * Customer list returns `id` = user id. Membership endpoint historically
 * returned `id` = membership row id, with `user_id` separately. We preserve
 * that contract by exposing both `id` (membership row id when known, falling
 * back to user id) and `user_id`.
 */
/** Strip display prefixes from cashless id strings shown in the UI. */
export const normalizeMembershipNumberForApi = (raw) => {
	if (raw == null || raw === "") return "";
	if (typeof raw === "object") {
		const nested = raw.membership_number ?? raw.number ?? raw.value ?? raw.id;
		if (nested != null && nested !== raw) {
			return normalizeMembershipNumberForApi(nested);
		}
		return "";
	}
	let s = String(raw).trim();
	if (s.startsWith("#")) s = s.slice(1).trim();
	return s;
};

const parseMembershipIdValue = (raw) => {
	if (raw == null || raw === "") return null;
	const num = Number(raw);
	return Number.isFinite(num) && num > 0 ? num : null;
};

/** Read internal `membership_id` from a `/users/customer/list` response. */
export const extractMembershipIdFromListResponse = (response) => {
	const clients =
		response?.data?.data?.list?.clients ??
		response?.data?.list?.clients ??
		response?.list?.clients ??
		[];
	const first = Array.isArray(clients) ? clients[0] : null;
	return parseMembershipIdValue(first?.membership_id ?? first?.membership?.id);
};

/** Resolve internal membership row id for password/payment APIs (not user id or membership_number). */
export const resolveMembershipIdForApi = (source) => {
	if (!source || typeof source !== "object") return null;
	return parseMembershipIdValue(source.membership_id ?? source.membership?.id);
};

/** `{ membership_id }` for `/bookings/product/payment` and booking payment APIs. */
export const buildMembershipIdPaymentFields = (membershipId) => {
	const parsed = parseMembershipIdValue(membershipId);
	if (parsed == null) return {};
	return { membership_id: parsed };
};

export const mapCustomerToMembershipClient = (customer) => {
	if (!customer || typeof customer !== "object") return null;
	const fullName = `${customer.name || ""} ${customer.lastname || ""}`.trim();
	const membershipNumber = normalizeMembershipNumberForApi(customer.membership_number);
	const resolvedMembershipId =
		customer.membership_id ?? customer.membership?.id ?? null;

	return {
		...customer,
		id: resolvedMembershipId ?? customer.id,
		user_id: customer.id,
		user_name: fullName || customer.name || "",
		user_phone: customer.contact_number || "",
		membership_id: resolvedMembershipId,
		membership_number: membershipNumber || null,
		credit_balance: customer.credit_balance ?? customer.current_credit_balance ?? 0,
		current_credit_balance:
			customer.current_credit_balance ?? customer.credit_balance ?? 0,
		is_password_set: customer.is_password_set,
	};
};
